import React, { FC, useContext, useEffect, useState } from "react";
import { uniqueId } from "lodash";
import * as helpers from "@app/exports/export-panel-body/exportPanelBodyHelpers";
import { ReportSettingsModel } from "@app/exports/exportModels";
import { ReportSettingsFlyout } from "./ReportSettingsFlyout";
import { FlyoutContext, FlyoutLink } from "@app/shared/Flyouts";
import FlyoutContextProvider from "@app/shared/Flyouts/FlyoutContext";
import { QESSelect, QESTextInput } from "@app/shared/qes-standards";

interface ReportSettingsBodyProps {
  buildReportSettingsModel: (isDefault: boolean, name: string) => ReportSettingsModel;
  createReportSettingsNameInputDisabled: boolean;
  currentReportSettings: ReportSettingsModel;
  isSelectionValid: boolean;
  parseReportSettingsJsonToState: (reportSettingsJson: string) => void;
  reportSettingsList: ReportSettingsModel[];
  reportSettingsHasChanges: boolean;
  reportSettingsHasInvalidChanges: boolean;
  saveClicked: boolean;
  setCreateReportSettingsNameInputDisabled: (disabled: boolean) => void;
  setCurrentReportSettings: (rs: ReportSettingsModel) => void;
  setReportSettingsIsSelectionValid: (isSelectionValid: boolean) => void;
  setReportSettingsHasChanges: (hasChanges: boolean) => void;
  setReportSettingsHasInvalidChanges: (hasInvalidChanges: boolean) => void;
  setReportSettingsList: (rs: ReportSettingsModel[]) => void;
  setSaveClicked: (clicked: boolean) => void;
}

const WrappedReportSettingsBody: React.FC<React.PropsWithChildren<ReportSettingsBodyProps>> = (props: ReportSettingsBodyProps) => {
  const reportSettingsNameInputId = uniqueId("report-settings-name-input-");
  const reportSettingsDropdownId = uniqueId("report-settings-dropdown-");
  const {
    buildReportSettingsModel,
    createReportSettingsNameInputDisabled,
    currentReportSettings,
    isSelectionValid,
    parseReportSettingsJsonToState,
    reportSettingsHasChanges,
    reportSettingsHasInvalidChanges,
    reportSettingsList,
    saveClicked,
    setCreateReportSettingsNameInputDisabled,
    setCurrentReportSettings,
    setReportSettingsIsSelectionValid,
    setReportSettingsHasChanges,
    setReportSettingsHasInvalidChanges,
    setReportSettingsList,
    setSaveClicked,
  } = props;
  const flyoutContext = useContext(FlyoutContext);

  const [suggestedName, setSuggestedName] = useState(helpers.reportName("ReportSettings"));

  useEffect(() => {
    setSuggestedName(helpers.reportName("ReportSettings"));
  }, [
    createReportSettingsNameInputDisabled,
    currentReportSettings,
    isSelectionValid,
    reportSettingsHasChanges,
    reportSettingsHasInvalidChanges,
    reportSettingsList,
    saveClicked
  ]);

  const handleReportSettingsNameChange = (name: string) => {
    if (!isNameOk(name))
      setReportSettingsHasChanges(false); // disable the 'Save Settings' button
    else
      setReportSettingsHasChanges(true);
    setCurrentReportSettings({ ...currentReportSettings, name });
  }

  const isNameOk = (name: string): boolean => name && name.length > 0 && name.length < 255;
  const getSelectionDisplayName = (rs: ReportSettingsModel): string => {
    if (rs)
      return `${rs.name} ${rs.isDefault ? "- (Default)" : ""}`;

    return "Select an option";
  }

  const handleReportSettingsChange = (id: string) => {
    if (id === "-1") {
      handleCreateNewReportSettingsClick();
      return;
    } else if (isNaN(parseInt(id, 10))) {
      return;
    }

    const selectedSettings = reportSettingsList.find(rs => rs.id === parseInt(id, 10));
    if (selectedSettings) {
      setReportSettingsHasChanges(false);
      setCreateReportSettingsNameInputDisabled(true);
      setCurrentReportSettings(selectedSettings);
      setSaveClicked(false);
      parseReportSettingsJsonToState(selectedSettings.reportSettingsJson);
    }
  }

  const handleCreateNewReportSettingsClick = () => {
    flyoutContext.setIsFlyoutVisible(false);
    setReportSettingsHasChanges(true);
    setReportSettingsIsSelectionValid(true);
    setSaveClicked(false);
    setCreateReportSettingsNameInputDisabled(false);
    setCurrentReportSettings(buildReportSettingsModel(false, suggestedName));
  }

  return (
    <>
      <div className="row">
        <p className="lead mb1 font-weight-bold col-12">Report Settings</p>
        <div className="col-12 col-sm-6">
          <>
            <div>
              <QESSelect
                label="Apply saved settings to this report:"
                id={reportSettingsDropdownId}
                testId={reportSettingsDropdownId}
                errorText="Invalid settings selected. Settings have been reset."
                isValid={!(currentReportSettings && !isSelectionValid)}
                required={true}
                onChange={(e) => { handleReportSettingsChange(e.target.value) }}
                value={currentReportSettings?.id || currentReportSettings?.name || "-2"}
              >
                <option value="-2">Select an option</option>
                <option value="-1" className="font-italic">Create New</option>
                {reportSettingsList.length > 0 && reportSettingsList.map((rs) => (
                  <option key={rs.id} value={rs.id}>{getSelectionDisplayName(rs)}</option>
                ))}
                {!!currentReportSettings && !reportSettingsList.find((rs) => rs.id === currentReportSettings.id) && <option value={currentReportSettings.id || currentReportSettings.name}>{currentReportSettings.name}</option>}
              </QESSelect>

              <FlyoutLink
                classNames="qes-button-secondary float-right"
                content={{
                  body: (
                    <ReportSettingsFlyout
                      currentReportSettings={currentReportSettings}
                      getSelectionDisplayName={getSelectionDisplayName}
                      handleCreateNewReportSettingsClick={handleCreateNewReportSettingsClick}
                      isNameOk={isNameOk}
                      reportSettingsHasChanges={reportSettingsHasChanges}
                      reportSettingsHasInvalidChanges={reportSettingsHasInvalidChanges}
                      reportSettingsList={reportSettingsList}
                      saveClicked={saveClicked}
                      setCurrentReportSettings={setCurrentReportSettings}
                      setReportSettingsHasChanges={setReportSettingsHasChanges}
                      setReportSettingsHasInvalidChanges={setReportSettingsHasInvalidChanges}
                      setReportSettingsList={setReportSettingsList}
                      setSaveClicked={setSaveClicked}
                    />
                  ),
                  title: "Manage Report Settings",
                }}
                size="20"
                warnOnClose={reportSettingsHasChanges}
                modalContent={{
                  body: "You will lose unsaved changes. Continue with closing the sidebar?",
                  cancelButtonText: "Cancel",
                  confirmButtonText: "Continue",
                  title: "Warning",
                }}
                testId="reporting-settings-body__manage-flyout"
              >
                Manage Settings
              </FlyoutLink>
            </div>
          </>
        </div>
        <div className="col-12 col-sm-6">
          <QESTextInput
            label="Create settings name:"
            disabled={createReportSettingsNameInputDisabled}
            id={reportSettingsNameInputId}
            onChange={(e) => handleReportSettingsNameChange(e.target.value)}
            value={currentReportSettings ? currentReportSettings.name : suggestedName}
            testId="report-settings-body__name-input"
            required={true}
            isValid={isNameOk(currentReportSettings ? currentReportSettings.name : suggestedName)}
            errorText="Invalid report settings name, please enter a name between 1 and 255 characters."
          />
        </div>
      </div>
      <hr className="my0" />
    </>
  );
}

export const ReportSettingsBody: FC<ReportSettingsBodyProps> = (props: ReportSettingsBodyProps) => {
  return (
    <FlyoutContextProvider>
      <WrappedReportSettingsBody {...props} />
    </FlyoutContextProvider>
  )
}